import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Clear, DeleteOutline } from '@mui/icons-material';
import {
  deleteOwnSubmittedAction,
  rejectSubmittedAction,
} from '../graphql/mutations';
import { API } from '../services/amplifyCompat';
import { captureException, logger, startSpan } from '../monitoring/sentry';
import useTranslation from './customHooks/translations';
import { useAuthenticatedMediaUrl } from '../hooks/use-authenticated-media-url';

const SubmittedActionCard = ({
  action,
  showUnapproveButton,
  showDeleteButton,
  refreshUserActions,
  onActionFeedback,
  getActions,
}) => {
  const translation = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const {
    action_name,
    date_of_action,
    g_co2_saved,
    image_validation_status,
    is_rejected,
    is_validated,
    points_earned,
    submitted_action_items,
    submitted_image,
    time_submitted,
    name_of_user,
  } = action;
  // While the photo is being checked (or flagged for an admin to look at),
  // the image itself is not viewable, so show a status chip instead of a
  // broken image and make the delayed points feel intentional.
  const isAwaitingPhotoCheck =
    !is_validated &&
    !is_rejected &&
    ['pending_upload', 'pending'].includes(image_validation_status);
  const isFlaggedForReview =
    !is_validated && !is_rejected && image_validation_status === 'unsafe';
  const submittedImageUrl = useAuthenticatedMediaUrl(
    isAwaitingPhotoCheck || isFlaggedForReview ? null : submitted_image
  );
  const refreshActions = useCallback(() => {
    const refresh = refreshUserActions ?? getActions ?? (() => {});
    return refresh();
  }, [getActions, refreshUserActions]);

  const rejectAction = async () => {
    await API.graphql({
      query: rejectSubmittedAction,
      variables: { sa_id: action.sa_id },
    });

    refreshActions();
  };

  const handleDeleteAction = async () => {
    return startSpan(
      {
        op: 'ui.action',
        name: 'Delete Own Submitted Action',
      },
      async (span) => {
        span.setAttribute('feature', 'self-serve-entry-delete');
        span.setAttribute('submitted_action.sa_id', action.sa_id);
        span.setAttribute('submitted_action.is_rejected', Boolean(is_rejected));

        setIsDeleting(true);
        setDeleteError('');

        try {
          await API.graphql({
            query: deleteOwnSubmittedAction,
            variables: { sa_id: action.sa_id },
          });

          await refreshActions();
          setShowDeleteDialog(false);
          onActionFeedback?.({
            severity: 'success',
            message: translation.deleteEntrySuccess,
          });
          logger.info('Self-serve submitted action deleted', {
            feature: 'self-serve-entry-delete',
            saId: action.sa_id,
            wasRejected: Boolean(is_rejected),
          });
        } catch (error) {
          captureException(error, {
            tags: {
              module: 'SubmittedActionCard',
              feature: 'self-serve-entry-delete',
              action: 'delete-own-submitted-action',
            },
            extra: {
              saId: action.sa_id,
              wasRejected: Boolean(is_rejected),
              actionName: action_name,
            },
          });
          logger.error('Self-serve submitted action delete failed', {
            feature: 'self-serve-entry-delete',
            saId: action.sa_id,
            wasRejected: Boolean(is_rejected),
          });
          setDeleteError(translation.deleteEntryFailure);
        } finally {
          setIsDeleting(false);
        }
      }
    );
  };

  return (
    <>
      <Card sx={{ display: 'flex', overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%',
              alignItems: { xs: 'center', sm: 'stretch' },
            }}
          >
            {submittedImageUrl && (
              <CardMedia
                component="img"
                sx={{
                  width: 150,
                  filter: is_rejected && 'grayscale(100%)',
                }}
                image={submittedImageUrl}
                alt={`A submitted ${action_name} action image submitted at ${time_submitted}`}
              />
            )}
            <CardContent sx={{ ml: { xs: '0em', md: '2em' } }}>
              {(isAwaitingPhotoCheck || isFlaggedForReview) && (
                <Chip
                  size="small"
                  color={isAwaitingPhotoCheck ? 'warning' : 'default'}
                  label={
                    isAwaitingPhotoCheck
                      ? translation.pendingPhotoCheck
                      : translation.photoUnderReview
                  }
                  sx={{ mb: '1em' }}
                />
              )}
              <Typography variant="subtitle2" sx={{ mb: '1.5em' }}>
                {date_of_action.split('T')[0]}{' '}
                {name_of_user ? `- ${name_of_user}` : ''}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mt: { xs: '0.5em', md: '0', color: is_rejected && '#303030' },
                }}
              >
                {action_name}
              </Typography>
              <Typography sx={{ my: 1.5, color: '#7e7e7e' }}>
                {submitted_action_items}
              </Typography>
              <Typography variant="body1">
                {translation.co2SavedColon} {g_co2_saved} g
              </Typography>
              <Typography variant="body1">
                {translation.totalPointsEarnedColon} {points_earned}
              </Typography>
            </CardContent>
          </Box>
          {(showUnapproveButton || showDeleteButton) && (
            <CardActions
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                alignSelf: 'center',
                mb: { xs: '1em', sm: '0em' },
                mr: { xs: '0em', sm: '3em' },
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {showUnapproveButton && (
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    color="error"
                    onClick={rejectAction}
                  >
                    {translation.unapprove}
                  </Button>
                )}
                {showDeleteButton && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteOutline />}
                    color="error"
                    onClick={() => {
                      setDeleteError('');
                      setShowDeleteDialog(true);
                    }}
                    disabled={isDeleting}
                  >
                    {translation.deleteEntry}
                  </Button>
                )}
              </Stack>
            </CardActions>
          )}
        </Box>
      </Card>
      <Dialog
        aria-labelledby="delete-submitted-action-dialog"
        open={showDeleteDialog}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteDialog(false);
            setDeleteError('');
          }
        }}
      >
        <DialogTitle id="delete-submitted-action-dialog">
          {translation.deleteEntryConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>{translation.deleteEntryConfirmBody}</Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteDialog(false);
              setDeleteError('');
            }}
            disabled={isDeleting}
          >
            {translation.cancel}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteAction}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutline />}
          >
            {isDeleting ? translation.deletingEntry : translation.deleteEntry}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubmittedActionCard;
